/**
 * Timestamps of unknown unit (FE-16, XS-6).
 *
 * The wire mixes seconds (payload `timestamp`), milliseconds (`Date.now()`),
 * microseconds and nanoseconds (REST `created_timestamp`). The unit is read from
 * the number of INTEGER digits, which for any date between 2001 and 2286 is
 * unambiguous: 10 = s, 13 = ms, 16 = µs, 19 = ns. The old rule padded anything
 * up to 13 digits to 19 and passed longer values through, so a µs value was
 * treated as ns and sorted 1000x into the past.
 */

export type TimestampUnit = "s" | "ms" | "us" | "ns";

export const detectTimestampUnit = (integerDigits: string): TimestampUnit => {
  if (integerDigits.length <= 10) return "s";
  if (integerDigits.length <= 13) return "ms";
  if (integerDigits.length <= 16) return "us";
  return "ns";
};

const DIGITS_TO_NANOS: Record<TimestampUnit, number> = { s: 9, ms: 6, us: 3, ns: 0 };

/**
 * An integer nanosecond string for a non-negative timestamp in any of the four
 * units (fractional parts kept down to the nanosecond), or `undefined` when the
 * value is not a plain non-negative number.
 */
export const toNanosString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "number" && !Number.isFinite(value)) return undefined;
  const text =
    typeof value === "number" ? value.toFixed(value % 1 === 0 ? 0 : 9) : String(value).trim();
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return undefined;

  const integer = match[1].replace(/^0+(?=\d)/, "");
  const zeros = DIGITS_TO_NANOS[detectTimestampUnit(integer)];
  const fraction = `${match[2] || ""}${"0".repeat(zeros)}`.slice(0, zeros);
  return `${integer}${fraction}`.replace(/^0+(?=\d)/, "");
};

/** Milliseconds since the epoch for a timestamp in any unit, or `undefined`. */
export const toMillis = (value: unknown): number | undefined => {
  const nanos = toNanosString(value);
  if (nanos === undefined) return undefined;
  return Number(BigInt(nanos) / BigInt(1000000));
};
